import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Plus } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { useEntitlements, barberCapacity } from '../lib/entitlements';
import { usePricing } from '../lib/pricing';
import { DAYS, lookupAuthUidByEmail } from '../lib/barbers/helpers';
import { BuyExtraModal } from '../components/barbers/BuyExtraModal';
import { BarberFormModal } from '../components/barbers/BarberFormModal';
import { BarberList } from '../components/barbers/BarberList';
import { ScheduleHoursPanel } from '../components/barbers/ScheduleHoursPanel';
import './Barbers.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BarbersInner = () => {
  const { plan, shop } = useEntitlements();
  const { pricing } = usePricing();
  const extraPrice = pricing.extraBarberPrice;
  const premiumBase = pricing.premium.price;
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBarber, setEditingBarber] = useState(null); // null=add, obj=edit
  const [formData, setFormData] = useState({ name: '', role: '', bio: '', notification_email: '', phone: '', can_manage_bookings: false, can_take_payments: false, is_also_barber: false });
  const [giveLogin, setGiveLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [ownerAuthUid, setOwnerAuthUid] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownAsBarber, setOwnAsBarber] = useState(false);
  const [callerRole, setCallerRole] = useState(null);

  // Batch 3b.2 — edit-modal login-side state
  const [authState, setAuthState] = useState(null); // {has_login,email,pending_email}
  const [newLoginEmail, setNewLoginEmail] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginMsg, setLoginMsg] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarErr, setAvatarErr] = useState('');
  // Cache-busting version tag on the preview <img> — bumps when a new
  // upload succeeds so the browser fetches the fresh bytes instead of
  // the cached 1-hour Cache-Control response.
  const [avatarV, setAvatarV] = useState(0);
  // Which user_ids in this shop are shop_admins? Used to badge the barber list.
  const [shopAdminUids, setShopAdminUids] = useState(new Set());
  const [menuOpenId, setMenuOpenId] = useState(null); // barber-list kebab menu (Edit / Remove)
  // "This is me" appears only for shop admins whose auth uid isn't already
  // linked to any barber row in this shop.
  const iAmABarber = ownerAuthUid && barbers.some(b => b.user_id === ownerAuthUid);

  // Booking policy for the currently-selected barber (mirrors barbers row).
  const [policyNotice, setPolicyNotice] = useState(24);
  const [policyWindow, setPolicyWindow] = useState(14);

  useEffect(() => {
    if (selectedBarber) {
      setPolicyNotice(selectedBarber.min_notice_hours ?? 24);
      setPolicyWindow(selectedBarber.booking_window_days ?? 14);
    }
  }, [selectedBarber]);

  // ----- Extras (paid additional barbers) -----
  // Local mirror of shops.extra_barbers so we can optimistically update
  // capacity after a successful purchase without needing to reload the
  // whole AppShell profile.
  const [extras, setExtras] = useState(shop?.extra_barbers || 0);
  const [showBuyExtraModal, setShowBuyExtraModal] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState('');
  const [postPurchaseBanner, setPostPurchaseBanner] = useState('');

  useEffect(() => {
    setExtras(shop?.extra_barbers || 0);
  }, [shop?.extra_barbers]);

  // Capacity is computed from a shop-shaped object that reflects the
  // *local* extras count (in case the profile hasn't refreshed yet).
  const capacityShop = { ...(shop || {}), plan, extra_barbers: extras };
  // Only barber-role rows count as chairs against the plan cap.
  // The owner's row (identified by user_id ∈ shopAdminUids — the
  // `role` column stores display labels like "Barber 1" not the auth
  // role) only counts when the admin flips the "I also cut hair"
  // toggle (is_also_barber). Item 28 — fixes the "shop admin counts
  // as a chair even though they never cut" reported in the manual
  // test session.
  const chairsCount = barbers.filter(b =>
    !(b.user_id && shopAdminUids.has(b.user_id)) || b.is_also_barber
  ).length;
  const capacity = barberCapacity(capacityShop, chairsCount, {
    includedBarbers: pricing[plan]?.includedBarbers,
    extraBarberPrice: extraPrice,
  });

  useEffect(() => {
    if (shop?.id) fetchBarbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id]);

  // Close the barber-row kebab menu on any outside click or on Escape.
  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest('.bcard-menu-root')) setMenuOpenId(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpenId(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpenId]);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setOwnerAuthUid(user.id);
      setOwnerEmail(user.email || '');
      const { data: prof } = await supabase
        .from('user_profiles')
        .select('full_name,role')
        .eq('id', user.id)
        .maybeSingle();
      if (prof) {
        setOwnerName(prof.full_name || '');
        setCallerRole(prof.role || null);
      }
      // Fetch all shop_admin uids in the caller's shop
      if (shop?.id) {
        const { data: admins } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('shop_id', shop.id)
          .eq('role', 'shop_admin');
        if (admins) setShopAdminUids(new Set(admins.map(a => a.id)));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id]);

  useEffect(() => {
    if (selectedBarber) {
      fetchBarberHours(selectedBarber.id);
    }
  }, [selectedBarber]);

  const fetchBarbers = async () => {
    if (!shop?.id) return;
    try {
      const { data, error } = await supabase
        .from('barbers')
        .select('*')
        .eq('shop_id', shop.id)
        .eq('active', true)
        .order('created_at');

      if (error) throw error;
      setBarbers(data || []);
      if (data && data.length > 0 && !selectedBarber) {
        setSelectedBarber(data[0]);
      }
    } catch (error) {
      console.error('Error fetching barbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBarberHours = async (barberId) => {
    try {
      const { data, error } = await supabase
        .from('barber_hours')
        .select('*')
        .eq('barber_id', barberId)
        .order('weekday');

      if (error) throw error;

      // Every barber must have exactly 7 rows in state, one per JS
      // weekday (0=Sun..6=Sat). If the DB has fewer (partial legacy
      // rows, or a barber added via a path that didn't seed hours),
      // top up the gaps in-memory with `is_open=false` placeholders.
      // Without this, `updateHour(weekday,...)` finds no matching row
      // and the click silently disappears — the original inert-toggle
      // bug. The backfill SQL closes this on the write side; this
      // guard keeps the UI honest for anything the SQL hasn't touched
      // (e.g. barbers created between backfill and next deploy).
      const byWeekday = new Map((data || []).map(r => [r.weekday, r]));
      const normalised = DAYS.map(day => byWeekday.get(day.value) || {
        barber_id: barberId,
        weekday: day.value,
        start_time: '09:00',
        end_time: '18:00',
        is_open: false,
      });
      setHours(normalised);
    } catch (error) {
      console.error('Error fetching barber hours:', error);
    }
  };

  const openAddForm = () => {
    setEditingBarber(null);
    setFormData({ name: '', role: '', bio: '', notification_email: '', phone: '', can_manage_bookings: false, can_take_payments: false, is_also_barber: false });
    setGiveLogin(false); setLoginEmail(''); setInviteMsg('');
    setOwnAsBarber(false);
    setAuthState(null); setNewLoginEmail(''); setLoginMsg(''); setLoginErr('');
    setAvatarErr('');
    setShowForm(true);
  };

  const openEditForm = async (barber) => {
    setEditingBarber(barber);
    // If we're opening the shop owner's row, force the role to "Barber 1"
    // so a save always migrates legacy labels (e.g. 'Owner') to the
    // canonical value. The select is also disabled to prevent edits.
    const isOwnerRow = !!barber.user_id && shopAdminUids.has(barber.user_id);
    setFormData({
      name: barber.name || '',
      role: isOwnerRow ? 'Barber 1' : (barber.role || ''),
      bio: barber.bio || '',
      // Notification email — falls back to legacy `email` so shops that
      // never set the split field still see their existing value.
      notification_email: barber.notification_email || barber.email || '',
      phone: barber.phone || '',
      // Per-barber capabilities. Both default to false so a brand-new
      // barber cannot mutate bookings or take payments through the
      // diary until the shop admin explicitly grants it.
      can_manage_bookings: !!barber.can_manage_bookings,
      can_take_payments:   !!barber.can_take_payments,
      // Owner-only "I also cut hair" — only meaningful on the
      // shop_admin row; hidden in the UI otherwise but round-tripped
      // through the modal so we don't accidentally clobber it.
      is_also_barber: !!barber.is_also_barber,
    });
    setGiveLogin(false); setLoginEmail(''); setInviteMsg('');
    setOwnAsBarber(false);
    setAvatarErr('');
    setAuthState(null); setNewLoginEmail(''); setLoginMsg(''); setLoginErr('');
    setShowForm(true);
    // Best-effort fetch of the login-side state — never blocks the modal.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/barber-auth-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ barber_id: barber.id }),
      });
      if (res.ok) setAuthState(await res.json());
    } catch { /* best-effort — modal still functional without auth state */ }
  };

  // Remove a barber. Backend endpoint enforces the guardrail (blocks
  // if any future non-cancelled bookings) and auto-decrements the
  // £8/mo extra-barber Stripe line-item so the shop stops overpaying.
  const [deletingBarberId, setDeletingBarberId] = useState(null);
  const handleDeleteBarber = async (barber) => {
    const hasLogin = !!barber.user_id;
    const confirmMsg =
      `Remove ${barber.name} from this shop?\n\n` +
      (hasLogin ? '• Their login account will also be removed.\n' : '') +
      '• Their weekly hours, breaks and date exceptions will be deleted.\n' +
      '• If you are on Premium and this is a paid extra barber, your £8/mo add-on will be refunded on your next invoice.\n' +
      '• Bookings in the past stay in the record; future bookings must be cancelled or reassigned first.';
    if (!window.confirm(confirmMsg)) return;

    setDeletingBarberId(barber.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/delete-barber`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ barber_id: barber.id, delete_auth_user: hasLogin }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // HTTP/2 often strips res.statusText, so fall back through the
        // JSON detail → statusText → HTTP status → generic string.
        const detail = typeof body.detail === 'string' ? body.detail : null;
        const reason = detail || res.statusText || `HTTP ${res.status}` || 'unknown error';
        alert(`Couldn't delete barber: ${reason}`);
        return;
      }
      // Optimistic + defensive: drop the row from local state
      // immediately so the UI reflects the delete without waiting on a
      // full re-fetch round-trip. `fetchBarbers()` below still runs to
      // reconcile any server-side changes we haven't accounted for.
      setBarbers((prev) => prev.filter((b) => b.id !== barber.id));
      if (selectedBarber?.id === barber.id) setSelectedBarber(null);
      if (typeof body.extras_new_count === 'number') setExtras(body.extras_new_count);
      const parts = [`${barber.name} removed.`];
      if (body.extras_updated && body.monthly_saving_gbp > 0) {
        parts.push(`£${body.monthly_saving_gbp.toFixed(2)}/mo saved — your next invoice will be prorated.`);
      }
      setPostPurchaseBanner(parts.join(' '));
      fetchBarbers();
    } catch (err) {
      alert(`Couldn't delete barber: ${err.message}`);
    } finally {
      setDeletingBarberId(null);
    }
  };

  const handleAddBarber = async (e) => {
    e.preventDefault();
    setInviteMsg('');
    try {
      // "This is me" shortcut — pre-fill owner fields and mark as linked.
      const asMe = ownAsBarber && !editingBarber;
      const finalName  = asMe ? (ownerName || 'Owner') : formData.name;
      const finalNotif = asMe ? ownerEmail : (formData.notification_email || null);
      const linkedUid  = asMe
        ? ownerAuthUid
        : (finalNotif ? await lookupAuthUidByEmail(finalNotif) : null);

      if (editingBarber) {
        // -------------- EDIT MODE --------------
        // `email` (login) is NEVER touched from this form — that goes
        // through the double-opt-in flow below. `notification_email`
        // is the only address the shop admin can freely change.
        // NB: `role` is NOT written in this Supabase update — it goes
        // through POST /api/barbers/:id/role so the backend enforces
        // the "Barber 1 = shop owner" invariant.
        const patch = {
          name: formData.name,
          bio: formData.bio,
          notification_email: formData.notification_email || null,
          phone: formData.phone || null,
        };
        // Per-barber capabilities — only shop_admin (or master_admin
        // impersonating one) is allowed to touch these. A barber
        // editing themselves cannot elevate their own permissions.
        if (callerRole === 'shop_admin' || callerRole === 'master_admin') {
          patch.can_manage_bookings = !!formData.can_manage_bookings;
          patch.can_take_payments   = !!formData.can_take_payments;
        }
        // Owner-only "I also cut hair" — only writable on the owner's
        // own row (identified via shopAdminUids since `role` stores a
        // display label like "Barber 1"). Persist unconditionally when
        // the modal shows the toggle so seat-count reconciliation is
        // deterministic.
        const isOwnerRow = !!editingBarber.user_id && shopAdminUids.has(editingBarber.user_id);
        if (isOwnerRow) {
          patch.is_also_barber = !!formData.is_also_barber;
        }
        // Auto-link user_id when notification email matches an auth user and none was set
        if (!editingBarber.user_id && formData.notification_email) {
          const uid = await lookupAuthUidByEmail(formData.notification_email);
          if (uid) patch.user_id = uid;
        }
        const { data, error } = await supabase
          .from('barbers')
          .update(patch)
          .eq('id', editingBarber.id)
          .select()
          .single();
        if (error) throw error;

        // If the role changed, PATCH it via the guarded endpoint. Only
        // shop_admins can call this successfully; regular barbers will
        // never reach this branch because the role select is disabled
        // for them.
        let finalRow = data;
        const roleChanged = (formData.role || '') !== (editingBarber.role || '');
        if (roleChanged) {
          const { data: { session } } = await supabase.auth.getSession();
          const res = await fetch(`${BACKEND_URL}/api/barbers/${editingBarber.id}/role`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token || ''}`,
            },
            body: JSON.stringify({ role: formData.role || null }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.detail || 'Could not update role');
          }
          finalRow = { ...data, role: formData.role || null };
        }
        setBarbers((prev) => prev.map(b => b.id === finalRow.id ? finalRow : b));
        setSelectedBarber(finalRow);
      } else {
        // -------------- ADD MODE --------------
        const payload = {
          shop_id: shop.id,
          name: finalName,
          role: formData.role,
          bio: formData.bio,
          // On create, seed both fields with the same address — the
          // shop admin can later split them via the edit modal.
          email: finalNotif,
          notification_email: finalNotif,
          phone: formData.phone || null,
        };
        // Only a shop_admin can seed permissions on a new barber; the
        // column defaults still catch anyone who bypasses this UI.
        if (callerRole === 'shop_admin' || callerRole === 'master_admin') {
          payload.can_manage_bookings = !!formData.can_manage_bookings;
          payload.can_take_payments   = !!formData.can_take_payments;
        }
        if (linkedUid) payload.user_id = linkedUid;

        const { data, error } = await supabase
          .from('barbers')
          .insert([payload])
          .select()
          .single();
        if (error) throw error;

        // Seed 7 default barber_hours rows so every future weekday
        // toggle on the Weekly Hours panel has a row to mutate.
        // Platform rule: a barber has exactly 7 rows; a closed day is
        // is_open=false, never a missing row. Non-fatal on failure —
        // fetchBarberHours normalises on load as a second line of
        // defence, and `Save hours` upserts anyway.
        try {
          const defaultHours = DAYS.map(day => ({
            barber_id: data.id,
            weekday: day.value,
            start_time: '09:00',
            end_time: '18:00',
            is_open: day.value !== 0,   // closed Sunday by default
          }));
          const { error: hrErr } = await supabase.from('barber_hours').insert(defaultHours);
          if (hrErr) console.warn('Seeding default barber_hours failed:', hrErr.message);
        } catch (seedErr) {
          console.warn('Seeding default barber_hours threw:', seedErr);
        }

        // Optionally invite a login (non-fatal). Skipped if we linked owner.
        if (!asMe && giveLogin && loginEmail.trim()) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch(`${BACKEND_URL}/api/auth/create-barber`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
              body: JSON.stringify({
                email: loginEmail.trim().toLowerCase(),
                barber_id: data.id,
                name: finalName,
              }),
            });
            if (!res.ok) {
              const t = await res.text();
              setInviteMsg(`Barber saved, but login invite failed: ${t}`);
            } else {
              setInviteMsg(`Login invite emailed to ${loginEmail.trim()}.`);
            }
          } catch (err) {
            setInviteMsg(`Barber saved, but login invite failed: ${err.message}`);
          }
        }
        setSelectedBarber(data);
      }

      setShowForm(false);
      setEditingBarber(null);
      setFormData({ name: '', role: '', bio: '', notification_email: '', phone: '', can_manage_bookings: false, can_take_payments: false, is_also_barber: false });
      setGiveLogin(false); setLoginEmail(''); setOwnAsBarber(false);
      fetchBarbers();
    } catch (error) {
      console.error('Error saving barber:', error);
      alert('Error saving barber: ' + error.message);
    }
  };

  // Batch 3b.2 — avatar upload / clear
  const handleAvatarChange = async (file) => {
    if (!editingBarber || !file) return;
    setAvatarErr(''); setAvatarBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/barbers/${editingBarber.id}/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: fd,
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Upload failed (${res.status})`);
      const patched = { ...editingBarber, avatar_url: body.avatar_url };
      setEditingBarber(patched);
      setBarbers(prev => prev.map(b => b.id === patched.id ? patched : b));
      if (selectedBarber?.id === patched.id) setSelectedBarber(patched);
      setAvatarV(v => v + 1);
    } catch (e) {
      setAvatarErr(e.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarClear = async () => {
    if (!editingBarber) return;
    setAvatarErr(''); setAvatarBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/barbers/${editingBarber.id}/avatar`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const raw = await res.text();
        throw new Error(raw || `Clear failed (${res.status})`);
      }
      const patched = { ...editingBarber, avatar_url: null };
      setEditingBarber(patched);
      setBarbers(prev => prev.map(b => b.id === patched.id ? patched : b));
      if (selectedBarber?.id === patched.id) setSelectedBarber(patched);
      setAvatarV(v => v + 1);
    } catch (e) {
      setAvatarErr(e.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  // Batch 3b.2 — trigger a password-reset for the barber (shop_admin
  // never sees the password; barber gets a Resend email with a recovery
  // link that lands them on /reset).
  const handleSendReset = async () => {
    if (!editingBarber) return;
    setLoginBusy(true); setLoginMsg(''); setLoginErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/send-barber-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ barber_id: editingBarber.id }),
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Reset failed (${res.status})`);
      setLoginMsg(`Reset link sent to ${body.email || 'the barber'}.`);
    } catch (e) {
      setLoginErr(e.message);
    } finally {
      setLoginBusy(false);
    }
  };

  // Resend credentials — issues the barber a fresh temporary password
  // and emails it via the same template used at first invite. The
  // barber is forced to change it on next sign-in (must_change_password
  // flag). Uses the existing /resend-barber-credentials endpoint.
  const handleResendCreds = async () => {
    if (!editingBarber?.user_id) {
      setLoginErr('This barber has no login yet — add one first.');
      return;
    }
    setLoginBusy(true); setLoginMsg(''); setLoginErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/resend-barber-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ user_id: editingBarber.user_id }),
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Resend failed (${res.status})`);
      setLoginMsg(`New credentials emailed to ${body.email || 'the barber'}. They'll be forced to change it on next sign-in.`);
    } catch (e) {
      setLoginErr(e.message);
    } finally {
      setLoginBusy(false);
    }
  };

  // Batch 3b.2 — kick off a login-email change. Supabase mails a verify
  // link to the NEW address; the old login keeps working until they
  // click it so a typo can't lock the barber out.
  const handleChangeLoginEmail = async () => {
    if (!editingBarber || !newLoginEmail.trim()) return;
    setLoginBusy(true); setLoginMsg(''); setLoginErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/change-barber-login-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ barber_id: editingBarber.id, new_email: newLoginEmail.trim().toLowerCase() }),
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Email change refused (${res.status})`);
      setLoginMsg(body.message || 'Verification sent to the new address.');
      setNewLoginEmail('');
      setAuthState(prev => ({ ...(prev || {}), pending_email: body.pending_email }));
    } catch (e) {
      setLoginErr(e.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const handleSaveHours = async () => {
    try {
      // Persist booking policy dropdowns alongside the hours grid.
      await supabase
        .from('barbers')
        .update({ min_notice_hours: policyNotice, booking_window_days: policyWindow })
        .eq('id', selectedBarber.id);

      // Delete existing hours
      await supabase
        .from('barber_hours')
        .delete()
        .eq('barber_id', selectedBarber.id);

      // Insert new hours
      const hoursToInsert = hours.map(h => ({
        barber_id: selectedBarber.id,
        weekday: h.weekday,
        start_time: h.start_time,
        end_time: h.end_time,
        is_open: h.is_open,
      }));

      const { error } = await supabase
        .from('barber_hours')
        .insert(hoursToInsert);

      if (error) throw error;
      alert('Hours saved successfully!');
      fetchBarberHours(selectedBarber.id);
      // Refresh selectedBarber so the policy dropdowns reflect the saved values.
      const { data: refreshed } = await supabase
        .from('barbers').select('*').eq('id', selectedBarber.id).single();
      if (refreshed) {
        setSelectedBarber(refreshed);
        setBarbers((prev) => prev.map(b => b.id === refreshed.id ? refreshed : b));
      }
    } catch (error) {
      console.error('Error saving hours:', error);
      alert('Error saving hours: ' + error.message);
    }
  };

  const updateHour = (weekday, field, value) => {
    // Upsert-safe. If no row exists for this weekday (defence in
    // depth against a future load path that returns fewer than 7
    // rows), append a fresh one instead of silently discarding the
    // click. Every barber_hours row we create locally carries
    // `barber_id` so the subsequent `saveHours` INSERT is valid.
    setHours((prev) => {
      const idx = prev.findIndex(h => h.weekday === weekday);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], [field]: value };
        return next;
      }
      return [...prev, {
        barber_id: selectedBarber?.id,
        weekday,
        start_time: '09:00',
        end_time: '18:00',
        is_open: false,
        [field]: value,
      }];
    });
  };

  const handleAddClick = () => {
    setPostPurchaseBanner('');
    // Under cap: open the form directly
    if (capacity.underCap) {
      openAddForm();
      return;
    }
    // At cap on Standard: nudge already renders in the body; do nothing here.
    if (plan === 'standard') return;
    // At cap on Premium with room to buy an extra: open the confirmation.
    if (capacity.canBuyExtra) {
      setBuyError('');
      setShowBuyExtraModal(true);
    }
  };

  const handleBuyExtraConfirm = async () => {
    setBuying(true);
    setBuyError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const targetExtras = extras + 1;
      const res = await fetch(`${BACKEND_URL}/api/billing/set-extras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ shop_id: shop?.id, extras: targetExtras }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add extra barber slot');
      setExtras(targetExtras);
      setShowBuyExtraModal(false);
      // Auto-open the regular add-barber form now that capacity is unlocked
      setShowForm(true);
      const totalExtraCost = targetExtras * extraPrice;
      setPostPurchaseBanner(
        data.stripe_synced
          ? `Extra slot unlocked. £${totalExtraCost}/mo will be added to your subscription (prorated on your next invoice).`
          : `Extra slot reserved. £${totalExtraCost}/mo will be billed once your Premium subscription is active.`
      );
    } catch (e) {
      setBuyError(e.message);
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading barbers...</div>;
  }

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Barbers</h2>
          <div className="sub">
            {chairsCount} of {capacity.cap} used
            {capacity.extras > 0 && (
              <> · <span data-testid="barbers-extras-count">{capacity.extras} extra · £{capacity.extras * extraPrice}/mo</span></>
            )}
            {' · '}<span style={{ textTransform: 'capitalize' }}>{plan}</span>
          </div>
        </div>
        <button
          className="btn p"
          style={{ marginLeft: 'auto' }}
          onClick={handleAddClick}
          disabled={
            // Standard shops are still hard-capped
            (plan === 'standard' && chairsCount >= capacity.cap) ||
            // Premium: only block if beyond max-with-extras hard ceiling
            (plan === 'premium' && !capacity.canBuyExtra && chairsCount >= capacity.cap)
          }
          data-testid="add-barber-btn"
        >
          <Plus size={16} />
          {plan === 'premium' && capacity.atCap && capacity.canBuyExtra
            ? `Add barber (+£${extraPrice}/mo)`
            : 'Add barber'}
        </button>
      </div>

      <div className="body">
        {postPurchaseBanner && (
          <div
            className="upgrade-nudge"
            style={{ marginBottom: 14 }}
            data-testid="barbers-extra-purchased-banner"
          >
            <span>{postPurchaseBanner}</span>
            <button
              type="button"
              className="btn s"
              onClick={() => setPostPurchaseBanner('')}
              data-testid="barbers-extra-banner-dismiss"
            >Dismiss</button>
          </div>
        )}
        {plan === 'standard' && chairsCount >= capacity.cap && (
          <div className="upgrade-nudge" style={{ marginBottom: 14 }} data-testid="barbers-upgrade-nudge">
            <span><b>Standard</b> includes {pricing.standard.includedBarbers} barber{pricing.standard.includedBarbers === 1 ? '' : 's'}. Upgrade to Premium for {pricing.premium.includedBarbers} (+£{extraPrice}/mo per extra).</span>
            <Link to="/billing" className="btn p">Upgrade →</Link>
          </div>
        )}

        {showBuyExtraModal && (
          <BuyExtraModal
            capacity={capacity}
            extras={extras}
            extraPrice={extraPrice}
            premiumBase={premiumBase}
            buying={buying}
            buyError={buyError}
            onCancel={() => setShowBuyExtraModal(false)}
            onConfirm={handleBuyExtraConfirm}
          />
        )}

        {showForm && (
          <BarberFormModal
            formData={formData}
            setFormData={setFormData}
            editingBarber={editingBarber}
            handleAddBarber={handleAddBarber}
            onClose={() => { setShowForm(false); setEditingBarber(null); }}
            ownAsBarber={ownAsBarber}
            setOwnAsBarber={setOwnAsBarber}
            ownerName={ownerName}
            ownerEmail={ownerEmail}
            iAmABarber={iAmABarber}
            callerRole={callerRole}
            shopAdminUids={shopAdminUids}
            shop={shop}
            giveLogin={giveLogin}
            setGiveLogin={setGiveLogin}
            loginEmail={loginEmail}
            setLoginEmail={setLoginEmail}
            inviteMsg={inviteMsg}
            avatarV={avatarV}
            avatarBusy={avatarBusy}
            avatarErr={avatarErr}
            handleAvatarChange={handleAvatarChange}
            handleAvatarClear={handleAvatarClear}
            authState={authState}
            newLoginEmail={newLoginEmail}
            setNewLoginEmail={setNewLoginEmail}
            loginBusy={loginBusy}
            loginMsg={loginMsg}
            loginErr={loginErr}
            handleChangeLoginEmail={handleChangeLoginEmail}
            handleSendReset={handleSendReset}
            handleResendCreds={handleResendCreds}
          />
        )}

        <div className="barb-grid">
          <BarberList
            barbers={barbers}
            selectedBarber={selectedBarber}
            setSelectedBarber={setSelectedBarber}
            shopAdminUids={shopAdminUids}
            menuOpenId={menuOpenId}
            setMenuOpenId={setMenuOpenId}
            openEditForm={openEditForm}
            handleDeleteBarber={handleDeleteBarber}
            deletingBarberId={deletingBarberId}
          />

          {selectedBarber && (
            <ScheduleHoursPanel
              selectedBarber={selectedBarber}
              hours={hours}
              updateHour={updateHour}
              handleSaveHours={handleSaveHours}
              policyNotice={policyNotice}
              setPolicyNotice={setPolicyNotice}
              policyWindow={policyWindow}
              setPolicyWindow={setPolicyWindow}
            />
          )}
        </div>
      </div>
    </main>
  );
};

const Barbers = () => (
  <AppShell>
    <BarbersInner />
  </AppShell>
);

export default Barbers;
